import { MarketplaceDbIntegration } from 'generated/resources/datatable.graphql'
import { ReadSyncValidationsQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

export type SyncIssue = {
  title: string
  description: string
  buttonLabel?: string
  buttonUrl?: string
}

type SyncValidationResult = NonNullable<
  ReadSyncValidationsQuery['integrations']['syncValidationsConnection']['nodes'][0]
>['issues'][0]

export const useCompensationsSyncIssues = (
  validationResults: SyncValidationResult[],
  marketplaceIntegration: MarketplaceDbIntegration | null
): SyncIssue[] => {
  const { navigation } = useNavigation()
  const integrationSettingsUrl = navigation.discover.marketplace.item.settings.fullPath({
    id: marketplaceIntegration?.datoId ?? '0',
  })

  const issuesByKey: Record<string, SyncIssue> = {
    authentication_failed: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.authentication_failed.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.authentication_failed.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.authentication_failed.button_label'
      ),
      buttonUrl: integrationSettingsUrl,
    },
    missing_legal_entity_code: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_legal_entity_code.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_legal_entity_code.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_legal_entity_code.button_label'
      ),
      buttonUrl: integrationSettingsUrl,
    },
    missing_payroll_concept_code: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_payroll_concept_code.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_payroll_concept_code.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_payroll_concept_code.button_label'
      ),
      buttonUrl: integrationSettingsUrl,
    },
    missing_employee_code: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_code.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_code.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_code.button_label'
      ),
      buttonUrl: navigation.employees.fullPath(),
    },
    missing_employee_company_identifier: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_company_identifier.title',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_company_identifier.description',
        {
          integration_name: marketplaceIntegration?.displayName,
        }
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_employee_company_identifier.button_label'
      ),
      buttonUrl: navigation.employees.fullPath(),
    },
    missing_amount: {
      title: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_amount.title'
      ),
      description: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_amount.description'
      ),
      buttonLabel: i18n.t(
        'compensations.integrations.sync_validations.blocking_issues.missing_amount.button_label'
      ),
      buttonUrl: navigation.compensations.definition.fullPath(),
    },
  }

  return validationResults
    .map((result) => issuesByKey[result.errorMessage.key])
    .filter(Boolean)
}
